import {
  BackendCashSession,
  BackendCashSessionSummary,
  BackendCashTransaction,
  CashSession,
  CashSessionSummary,
  CashTransaction,
  DeclarationUpdatePayload,
  ListCashSessionsParams,
  ListCashSessionsResponse,
  ManualMovementPayload,
  OpenCashSessionPayload,
} from '@/@types/cash-management';
import { api } from '@/services/apiClient';
import {
  mapBackendCashSessionToCashSession,
  mapBackendCashTransactionToCashTransaction,
  mapBackendSummaryToSummary,
} from './cash-management-api.mapper';

export async function listCashSessions({
  page,
  perPage,
  search,
  status,
  startDate,
  endDate,
  sortBy,
  sort,
}: ListCashSessionsParams): Promise<ListCashSessionsResponse> {
  const { data } = await api.get('/cash_sessions', {
    params: {
      page,
      per_page: perPage,
      search,
      status: status || undefined,
      opened_at_start: startDate || undefined,
      opened_at_end: endDate || undefined,
      sort_by: sortBy,
      sort: sort ? sort.toUpperCase() : undefined,
    },
  });

  const cashSessions = data.content.map((session: BackendCashSession) =>
    mapBackendCashSessionToCashSession(session)
  );

  return { cashSessions, totalCount: data.total };
}

export async function getCashSession(id: string): Promise<CashSession> {
  const { data } = await api.get(`/cash_sessions/${id}`);

  return mapBackendCashSessionToCashSession(data);
}

export async function getCashSessionSummary(
  id: string
): Promise<CashSessionSummary> {
  const { data } = await api.get<BackendCashSessionSummary>(
    `/cash_sessions/${id}/summary`
  );

  return mapBackendSummaryToSummary(data);
}

export async function getCurrentCashSession(): Promise<CashSession | null> {
  const { data } = await api.get('/cash_sessions/current');

  return data ? mapBackendCashSessionToCashSession(data) : null;
}

export async function openCashSession({
  startingCash,
}: OpenCashSessionPayload): Promise<CashSession> {
  const { data } = await api.post('/cash_sessions', {
    starting_cash: startingCash,
  });

  return mapBackendCashSessionToCashSession(data);
}

export async function createManualMovement({
  type,
  documentTypeId,
  amount,
  description,
}: ManualMovementPayload): Promise<CashTransaction> {
  const { data } = await api.post<BackendCashTransaction>(
    '/cash_sessions/manual-movements',
    {
      type,
      document_type_id: documentTypeId,
      amount,
      description,
    }
  );

  return mapBackendCashTransactionToCashTransaction(data);
}

export async function cancelManualMovement(id: string): Promise<CashSession> {
  const { data } = await api.delete(`/cash_sessions/manual-movements/${id}`);

  return mapBackendCashSessionToCashSession(data);
}

export async function startReconciliation(id: string): Promise<CashSession> {
  const { data } = await api.patch(`/cash_sessions/${id}/start-reconciliation`);

  return mapBackendCashSessionToCashSession(data);
}

export async function updateDeclarations(
  id: string,
  payload: DeclarationUpdatePayload
): Promise<CashSession> {
  const { data } = await api.put(`/cash_sessions/${id}/declarations`, {
    declarations: payload.declarations.map((declaration) => ({
      document_type_id: declaration.documentTypeId,
      declared_amount: declaration.declaredAmount,
    })),
  });

  return mapBackendCashSessionToCashSession(data);
}

export async function revertCashSessionStatus(
  id: string
): Promise<CashSession> {
  const { data } = await api.patch(`/cash_sessions/${id}/revert-status`);

  return mapBackendCashSessionToCashSession(data);
}

export async function closeCashSession(id: string): Promise<CashSession> {
  const { data } = await api.patch(`/cash_sessions/${id}/close`);

  return mapBackendCashSessionToCashSession(data);
}
